<template>
  <client-only>
    <div v-if="combinedFiles.length" class="ProductFiles">
      <a id="ProductFiles" />
      <GenericAccordion
        :headline="$t('productPage.downloads')"
        :size="design === 'b2cLight' ? 'xl':'sm'"
      >
        <div
          class="text-text-subtle type-sm mb-8"
          :class="{
            'mt-8': design === 'b2cLight'
          }"
        >
          {{ $t('productPage.downloads.disclaimer') }}
        </div>

        <GenericAccordion
          v-for="group in combinedFiles"
          :key="group.code"
          :headline="group.title"
          :start-open="true"
        >
          <div class="w-full overflow-hidden">
            <a
              v-if="group.code === NorceMediaSetCodes.TDS && productItem.parametricByCode(NorceParametricCodes.ItemTDSlink)?.value"
              :href="productItem.parametricByCode(NorceParametricCodes.ItemTDSlink)?.value"
              :download="true"
              class="whitespace-nowrap block py-8 type-sm"
            >
              <img src="/icons/file-download.svg" class="w-12 inline-block">
              TDS
            </a>
            <a
              v-for="file in group.media"
              :key="file.id"
              :download="downloadName(file.name, file.fileType, file.url)"
              target="_blank"
              :href="`/filedownload/?origin=${file.url}&mimeType=${file.mimeType}`"
              class="whitespace-nowrap block py-8 type-sm"
            ><img src="/icons/file-download.svg" class="w-12 inline-block">
              {{ file.name }} <span v-if="!file.name || file.name === ''">{{ fallbackName(file.url) }}</span>
            </a>
            <br>
          </div>
        </GenericAccordion>
      </GenericAccordion>
    </div>
  </client-only>
</template>

<script setup lang="ts">
import { ProductModel } from '~/models/product';
import { ProductVariantModel } from '~/models/productVariant';
import { NorceMediaSetCodes, NorceParametricCodes } from '~/constants/norceCodes';
import { useGlobalContentStore } from '~/store/globalContent';
import { MediaSet } from '~/constants/types/algolia';

interface Props {
  productItem: ProductModel;
  activeVariant: ProductVariantModel,
  design?: 'standard' | 'b2cLight'
}

const props = withDefaults(defineProps<Props>(), {
  design: 'standard',
});

const globalContent = useGlobalContentStore();

const includedCodes = [
  NorceMediaSetCodes.TDS,
  NorceMediaSetCodes.InstallationDocs,
  NorceMediaSetCodes.CleaningAndMaintenanceDocs,
  NorceMediaSetCodes.DeclarationDocs,
  NorceMediaSetCodes.WarrantyDocs,
  NorceMediaSetCodes.CADImage,
  NorceMediaSetCodes.InteriorImage,
];

const fallbackName = (input: string) => {
  return input.split('/').at(-1);
};

const downloadName = (fileName: string | undefined, fileType: string, fileUrl: string) => {
  if (!fileName || fileName === '') {
    return fallbackName(fileUrl);
  }
  return `${fileName}${fileType}`;
};

// Filter documents based on language preferences
const filterDocumentsByLanguage = (media: MediaSet['media']) => {
  const locale = globalContent.getLocale;

  // UK: Show all documents that have a filename set
  if (locale === 'en-GB' || locale === 'en') {
    return media;
  }

  // DE/SE: Show local documents (without "(en)"). Show English "(en)" only if no local version exists
  const englishSuffix = '(en)';
  const localDocuments: MediaSet['media'] = [];
  const englishDocuments: (MediaSet['media'][0] & { baseName: string })[] = [];

  // First pass: identify documents and their English counterparts
  media.forEach(doc => {
    if (doc.name?.endsWith(englishSuffix)) {
      const baseName = doc.name.slice(0, -englishSuffix.length).trim();
      englishDocuments.push({ ...doc, baseName });
    } else {
      localDocuments.push(doc);
    }
  });

  // Second pass: filter English documents only if no local version exists
  const filteredEnglishDocs = englishDocuments.filter(englishDoc => {
    return !localDocuments.some(localDoc => localDoc.name === englishDoc.baseName);
  });

  return [...localDocuments, ...filteredEnglishDocs];
};

const combinedFiles = computed(()=> {

  let output = props.activeVariant.mediaSets || [];

  props.productItem.mediaSets?.forEach((variant) => {
    const exists = output.findIndex(pf => pf.code === variant.code);
    if (exists > -1 && Array.isArray(variant.media)) {
      //output[exists].media = output[exists].media.concat(variant.media);
      variant.media.forEach(vm => {
        const exists2 = output[exists].media.findIndex(f2 => f2.url === vm.url);
        if (exists2 === -1) {
          output[exists].media.push(vm);
        }
      });
    } else {
      output.push(variant);
    }
  });

  // Apply language filtering to each media set
  /*const filteredOutput = output.filter((f) => includedCodes.includes(f.code as NorceMediaSetCodes)).map(mediaSet => ({
    ...mediaSet,
    media: filterDocumentsByLanguage(mediaSet.media || []),
  }));*/

  const filteredOutput = output.filter((f) => includedCodes.includes(f.code as NorceMediaSetCodes)).map(mediaSet => ({
    ...mediaSet,
    media: filterDocumentsByLanguage(mediaSet.media || []).filter(media => media.name?.length > 0),
  })).filter(f => f.code === NorceMediaSetCodes.TDS || f.media.length > 0);

  return filteredOutput.sort((a, b) => {
    const aIndex = includedCodes.indexOf(a.code as NorceMediaSetCodes);
    const bIndex = includedCodes.indexOf(b.code as NorceMediaSetCodes);
    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });
});

</script>

<style scoped lang="postcss">
</style>
